import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const KarmicDebtNumberCalculator = () => {
  const [birthDate, setBirthDate] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const calculateKarmicDebt = () => {
    try {
      const date = new Date(birthDate);
      if (isNaN(date.getTime())) {
        setError('Please enter a valid date');
        return;
      }

      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();

      const numbers = [...day.toString(), ...month.toString(), ...year.toString()];
      const karmicDebts = [13, 14, 16, 19];
      const karmicNumbers = [];
      const calculationSteps = [];

      numbers.forEach((num, index) => {
        const fullNumber = parseInt(numbers.slice(0, index + 1).join(''));
        if (karmicDebts.includes(fullNumber)) {
          karmicNumbers.push(fullNumber);
          calculationSteps.push(`Found karmic debt number: ${fullNumber}`);
        }
      });

      const chartData = {
        labels: karmicDebts,
        datasets: [{
          label: 'Karmic Debt Numbers',
          data: karmicDebts.map(num => karmicNumbers.includes(num) ? 100 : 0),
          backgroundColor: [
            'rgba(54, 162, 235, 0.5)',
            'rgba(255, 99, 132, 0.5)',
            'rgba(75, 192, 192, 0.5)',
            'rgba(153, 102, 255, 0.5)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(153, 102, 255, 1)'
          ],
          borderWidth: 1
        }]
      };

      setResult({
        karmicNumbers,
        calculationSteps,
        interpretation: getInterpretation(karmicNumbers),
        chartData
      });
      setError('');
    } catch (err) {
      setError('An error occurred while calculating karmic debt numbers');
    }
  };

  const getInterpretation = (numbers) => {
    if (numbers.length === 0) {
      return "You have no karmic debt numbers, indicating a relatively smooth life path.";
    }

    const interpretations = {
      13: "Represents challenges in self-expression and creativity. You may face obstacles in expressing your true self.",
      14: "Indicates tests in freedom and self-discipline. You may struggle with finding balance between freedom and responsibility.",
      16: "Represents challenges in transformation and change. You may face significant life changes that require adaptation.",
      19: "Indicates tests in leadership and authority. You may face challenges in positions of power or leadership."
    };

    return numbers.map(num => interpretations[num]).join('\n\n');
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Karmic Debt Number Calculator - Numerology Analysis</title>
        <meta name="description" content="Calculate your karmic debt numbers using your birth date. Discover your life challenges and opportunities for growth through numerology." />
        <meta name="keywords" content="karmic debt, numerology calculator, birth date analysis, life path, spiritual growth" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Karmic Debt Number Calculator - Numerology Analysis" />
        <meta property="og:description" content="Calculate your karmic debt numbers using your birth date. Discover your life challenges and opportunities for growth through numerology." />
        <meta property="og:image" content="https://example.com/karmic-debt-og-image.jpg" />
        <meta property="og:url" content="https://example.com/karmic-debt-calculator" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Karmic Debt Number Calculator - Numerology Analysis" />
        <meta name="twitter:description" content="Calculate your karmic debt numbers using your birth date. Discover your life challenges and opportunities for growth through numerology." />
        <meta name="twitter:image" content="https://example.com/karmic-debt-og-image.jpg" />
      </Helmet>
      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🔮</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Karmic Debt Number Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Numerology Analysis</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          Calculate your karmic debt numbers using your birth date. Karmic debt numbers in numerology represent challenges 
          and lessons that you need to work through in this lifetime. They indicate areas where you may face obstacles 
          but also opportunities for growth and spiritual development.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="birthDate">
            Birth Date:
          </label>
          <input
            type="date"
            id="birthDate"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
          />
        </div>

        <button
          onClick={calculateKarmicDebt}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Karmic Debt Numbers
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {result && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Karmic Debt Numbers</h4>
                <div className="space-y-2">
                  {result.karmicNumbers.length > 0 ? (
                    result.karmicNumbers.map((number, index) => (
                      <p key={index} className="flex justify-between">
                        <span>Karmic Debt Number {index + 1}</span>
                        <span className="font-medium">{number}</span>
                      </p>
                    ))
                  ) : (
                    <p className="text-center text-gray-600">No karmic debt numbers found</p>
                  )}
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Karmic Debt Distribution</h4>
                <div className="h-96 relative">
                  <Bar
                    data={result.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      interaction: {
                        mode: 'index',
                        intersect: false
                      },
                      plugins: {
                        tooltip: {
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          titleFont: { size: 16, weight: 'bold' },
                          bodyFont: { size: 14 },
                          padding: 12,
                          cornerRadius: 4,
                          displayColors: false
                        },
                        legend: {
                          position: 'top',
                          labels: {
                            font: {
                              size: 14,
                              weight: '500'
                            }
                          }
                        },
                        title: {
                          display: true,
                          text: 'Karmic Debt Number Distribution',
                          font: {
                            size: 18,
                            weight: '600'
                          },
                          padding: {
                            bottom: 20
                          }
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          max: 100,
                          title: {
                            display: true,
                            text: 'Presence',
                            font: {
                              size: 14,
                              weight: '500'
                            }
                          },
                          grid: {
                            color: '#e5e7eb'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Karmic Debt Numbers',
                            font: {
                              size: 14,
                              weight: '500'
                            }
                          },
                          grid: {
                            display: false
                          }
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-8 bg-white p-6 rounded-xl shadow-md">
              <h4 className="text-xl font-semibold mb-4">Interpretation</h4>
              <div className="space-y-4">
                <p className="text-gray-700">{result.interpretation}</p>
              </div>
            </div>

            <div className="mt-8 bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-4">Calculation Steps</h4>
              <div className="space-y-4">
                {result.calculationSteps.map((step, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                    <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Karmic Debt Number Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Your Birth Date</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Select your birth date using the date picker</li>
                <li>Ensure the date is accurate for best results</li>
                <li>The calculator uses your exact birth date to determine karmic debt numbers</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Calculate Your Numbers</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Click the "Calculate Karmic Debt Numbers" button</li>
                <li>The calculator will analyze your birth date</li>
                <li>Results will be displayed in an easy-to-read format</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Interpret Your Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Review the karmic debt numbers found</li>
                <li>Read the detailed interpretation for each number</li>
                <li>Use the distribution chart to visualize your numbers</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Tips for Best Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Use your exact birth date for accurate calculations</li>
                <li>Consider the interpretations as guidance, not fate</li>
                <li>Use the results to understand potential challenges and growth areas</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      
      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Frequently Asked Questions <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">What are karmic debt numbers?</h3>
              <p className="text-gray-700">
                Karmic debt numbers are specific numbers in numerology that represent challenges and lessons you need to work through in this lifetime. 
                They indicate areas where you may face obstacles but also opportunities for growth and spiritual development.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">How are karmic debt numbers calculated?</h3>
              <p className="text-gray-700">
                Karmic debt numbers are derived from your birth date. The calculator analyzes the numbers in your birth date to identify 
                specific patterns that correspond to karmic debt numbers (13, 14, 16, and 19).
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">What if I don't have any karmic debt numbers?</h3>
              <p className="text-gray-700">
                Not having karmic debt numbers indicates a relatively smooth life path. This doesn't mean you won't face challenges, 
                but you may find it easier to navigate life's obstacles.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Are karmic debt numbers negative?</h3>
              <p className="text-gray-700">
                No, karmic debt numbers are not negative. They represent opportunities for growth and learning. Each number indicates 
                specific lessons that can lead to personal development when worked through.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">How can I use this information?</h3>
              <p className="text-gray-700">
                Understanding your karmic debt numbers can help you:
                <ul className="list-disc pl-8 space-y-2">
                  <li>Anticipate potential challenges</li>
                  <li>Prepare for growth opportunities</li>
                  <li>Develop strategies to overcome obstacles</li>
                  <li>Gain insight into your life path</li>
                  <li>Work on personal development areas</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Easy-to-use date picker interface</li>
                <li>Instant calculation of karmic debt numbers</li>
                <li>Detailed interpretations for each number</li>
                <li>Visual distribution chart</li>
                <li>Step-by-step calculation display</li>
                <li>Mobile-responsive design</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Gain insight into potential life challenges</li>
                <li>Understand areas for personal growth</li>
                <li>Visualize your karmic debt pattern</li>
                <li>Receive guidance for navigating obstacles</li>
                <li>Learn about your soul's lessons</li>
                <li>Track progress in overcoming challenges</li>
              </ul>
            </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Understanding Karmic Debt Numbers <span className="ml-2 text-2xl">📚</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Karmic debt numbers in numerology are specific numbers that indicate challenges and lessons you need to work through in this lifetime. 
            These numbers are derived from your birth date and represent areas where you may face obstacles but also opportunities for growth.
          </p>

          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Karmic Debt Numbers</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>
                  <strong>13:</strong> Challenges in self-expression and creativity
                </li>
                <li>
                  <strong>14:</strong> Tests in freedom and self-discipline
                </li>
                <li>
                  <strong>16:</strong> Challenges in transformation and change
                </li>
                <li>
                  <strong>19:</strong> Tests in leadership and authority
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Important Considerations</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Karmic debt numbers are not negative - they are opportunities for growth</li>
                <li>Each number represents a specific lesson to be learned</li>
                <li>Working through these challenges can lead to significant personal development</li>
                <li>Understanding your karmic debt numbers can help you navigate life's challenges more effectively</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KarmicDebtNumberCalculator;
